import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { ifcPersistence } from '@core/session/ifcPersistence'
import { fragmentPersistence } from '@core/session/fragmentPersistence'
import { modelAcceptAttr } from '@core/session/modelFileTypes'
import {
  abrirModeloPartilhado,
  useModelosPartilhados,
} from '@core/modelos/modelosPartilhados'
import { encontrarPorSha } from '@core/modelos/modelosRegras'

/**
 * Renders below the topbar when an Open Session call left behind a
 * list of IFCs the user expected to find loaded. The banner has two
 * paths:
 *
 *   1. Silent best-effort: if `ifcPersistence` (per-browser IDB
 *      cache) has the bytes for any expected SHA-256, load them
 *      automatically the moment the viewer canvas is ready.
 *
 *   2. Manual fallback: anything still missing after the auto-restore
 *      shows up in the banner. A single "Carregar do disco" button
 *      opens a multi-file picker so the user can re-supply every
 *      missing IFC in one go.
 *
 * The banner self-clears once every expected sha256 is back in the
 * viewer's models. Console output in [session-restore] makes it
 * easy to tell why a particular IFC didn't auto-load.
 */
export default function SessionRestoreBanner() {
  const expected = useAppStore((s) => s.sessionRestoreModels)
  const setExpected = useAppStore((s) => s.setSessionRestoreModels)
  // Subscrever `s.models` (referência estável) e derivar a lista de
  // hashes à parte. Devolver `s.models.map(...)` do próprio selector
  // criava um array novo em cada render: o zustand via sempre estado
  // "diferente" e entrava em ciclo infinito ("getSnapshot should be
  // cached" seguido de "Maximum update depth exceeded"). O sintoma só
  // não se via porque este banner estava desenhado num ramo do layout
  // que quase nunca corria.
  const models = useAppStore((s) => s.models)
  const loadedShas = useMemo(
    () => models.map((m) => m.sha256.toLowerCase()),
    [models],
  )
  const [busy, setBusy] = useState(false)
  const [statusByFile, setStatusByFile] = useState<Record<string, string>>({})
  const [autoRestoreState, setAutoRestoreState] = useState<
    'idle' | 'running' | 'done'
  >('idle')
  // Guarda PARA QUAL lista já se tentou a reposição automática, e não
  // um simples "já tentei". Assim invalida-se sozinho quando chega uma
  // lista nova, em vez de precisar de ser reposto durante o render,
  // que é coisa que não se faz a um ref.
  const tentadoParaRef = useRef<typeof expected>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  // Modelos partilhados no workspace: o que faltar na cache e estiver
  // partilhado vem de lá. Com o módulo ligado, espera-se pela lista
  // antes de tentar, para uma sessão leve não cair logo no "do disco".
  const modelosPart = useModelosPartilhados()
  const listaPartilhados = modelosPart?.lista ?? null
  const listaPronta =
    modelosPart === null || listaPartilhados !== null || modelosPart.erro !== null

  // Subscribe to viewerApi changes (loader registration, model add/
  // remove). Without this the banner mounts before the viewer canvas
  // and `hasIfcLoader()` is false on first render; the auto-restore
  // effect below would bail out and never re-fire. Re-rendering on
  // viewerApi changes lets the effect re-evaluate as soon as the
  // loader is registered.
  const loaderReady = useSyncExternalStore(
    (cb) => viewerApi.subscribe(cb),
    () => viewerApi.hasIfcLoader(),
    () => false,
  )

  const missing = useMemo(() => {
    if (!expected) return []
    const have = new Set(loadedShas)
    return expected.filter((m) => !have.has(m.sha256.toLowerCase()))
  }, [expected, loadedShas])

  // Silent auto-restore from the IDB byte cache. Files whose bytes
  // were primed by Open Session (or cached by a previous load) come
  // back without any user interaction.
  useEffect(() => {
    if (tentadoParaRef.current === expected) return
    if (!expected || missing.length === 0) return
    if (!loaderReady) {
      console.info('[session-restore] viewer loader not ready yet, waiting')
      return
    }
    if (!listaPronta) return
    tentadoParaRef.current = expected
    console.info(
      `[session-restore] auto-restore start, expecting ${missing.length} IFC(s)`,
    )
    void (async () => {
      // Marcar "a correr" aqui e não no corpo do efeito: é a mesma
      // instrução, mas fora do caminho síncrono que encadeia renders.
      setAutoRestoreState('running')
      const next: Record<string, string> = {}
      let restored = 0
      let cacheMissed = 0
      // O lote conta como "a carregar" também entre um IFC e o seguinte.
      const fecharLote = viewerApi.iniciarLoteDeCarregamento()
      try {
        for (const m of missing) {
          try {
            if (m.source_format === 'frag') {
              const fragment = await fragmentPersistence.load(m.sha256)
              if (!fragment) {
                cacheMissed++
                continue
              }
              const file = new File([fragment.blob], m.filename, {
                type: 'application/octet-stream',
                lastModified: new Date(fragment.createdAt).getTime(),
              })
              const abertoFrag = await viewerApi.loadIfcFile(file, { replace: false })
              if (!abertoFrag.ok) {
                next[m.sha256] = abertoFrag.erro
                continue
              }
              void fragmentPersistence.touch(m.sha256)
              next[m.sha256] = 'Fragments reaberto da cache'
              restored++
              continue
            }
            const rec = await ifcPersistence.load(m.sha256)
            const partilhado = rec ? null : encontrarPorSha(listaPartilhados, m.sha256)
            if (partilhado) {
              const r = await abrirModeloPartilhado(partilhado)
              if (r !== 'falhou') {
                next[m.sha256] = 'aberto dos modelos do workspace'
                restored++
              } else {
                next[m.sha256] = 'falhou ao abrir dos modelos do workspace'
              }
              continue
            }
            if (!rec) {
              cacheMissed++
              console.info(
                `[session-restore] no IDB cache for ${m.filename} (sha ${m.sha256.slice(0, 12)}…)`,
              )
              continue
            }
            console.info(
              `[session-restore] loading ${m.filename} from IDB cache (${rec.sizeBytes} bytes)`,
            )
            const aberto = await viewerApi.loadIfcFile(ifcPersistence.toFile(rec), {
              replace: false,
            })
            // A falha vem no resultado, não como excepção: sem isto o
            // ficheiro ficava na lista de falta com a nota "reaberto da cache".
            if (!aberto.ok) {
              next[m.sha256] = aberto.erro
              continue
            }
            void ifcPersistence.touch(m.sha256)
            next[m.sha256] = 'reaberto da cache'
            restored++
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'falhou ao reabrir'
            next[m.sha256] = msg
            console.warn(
              `[session-restore] auto-restore failed for ${m.filename}:`,
              err,
            )
          }
        }
      } finally {
        fecharLote()
      }
      console.info(
        `[session-restore] auto-restore done, restored=${restored}, cacheMissed=${cacheMissed}, total=${missing.length}`,
      )
      setStatusByFile((cur) => ({ ...cur, ...next }))
      setAutoRestoreState('done')
    })()
  }, [expected, missing, loaderReady, listaPronta, listaPartilhados])

  // Quando chega uma lista NOVA de ficheiros esperados, o estado da
  // tentativa anterior deixa de valer. Ajusta-se durante o render,
  // com guarda por referência, que é o padrão recomendado para isto:
  // fazê-lo no corpo de um efeito encadeia renders.
  const [ultimaLista, setUltimaLista] = useState(expected)
  if (expected !== ultimaLista) {
    setUltimaLista(expected)
    setStatusByFile({})
    setAutoRestoreState('idle')
  }

  // Limpar a lista assim que tudo estiver reposto. Isto escreve no
  // store e não em estado local, e é o que faz o banner desaparecer.
  useEffect(() => {
    if (expected && missing.length === 0) setExpected(null)
  }, [expected, missing.length, setExpected])

  if (!expected || missing.length === 0) return null

  // Multi-file IFC picker. Loads every picked file via the existing
  // pipeline; that pipeline computes the SHA-256, registers the model
  // in the store, and the `missing` selector above auto-updates as
  // `loadedShas` changes. We don't filter by expected SHA: if the
  // user picks a different IFC, that's fine; it just won't remove
  // anything from the missing list.
  const onPickFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return
    if (!viewerApi.hasIfcLoader()) {
      window.alert('Viewer ainda não está pronto. Tenta de novo num instante.')
      return
    }
    setBusy(true)
    const list = Array.from(files)
    void (async () => {
      const next: Record<string, string> = { ...statusByFile }
      for (const f of list) {
        try {
          const aberto = await viewerApi.loadIfcFile(f, { replace: false })
          const entry = expected.find(
            (e) => e.filename.toLowerCase() === f.name.toLowerCase(),
          )
          if (entry) next[entry.sha256] = aberto.ok ? 'carregado do disco' : aberto.erro
        } catch (err) {
          const entry = expected.find(
            (e) => e.filename.toLowerCase() === f.name.toLowerCase(),
          )
          const msg = err instanceof Error ? err.message : 'falhou ao carregar'
          if (entry) next[entry.sha256] = msg
          console.warn('[session-restore] manual reload failed for', f.name, err)
        }
      }
      setStatusByFile(next)
      setBusy(false)
    })()
  }

  const headerCopy = (() => {
    if (autoRestoreState === 'running') {
      return 'Sessão restaurada. A reabrir IFCs guardados…'
    }
    if (missing.length === 1) {
      return 'Sessão restaurada. Falta abrir 1 IFC do disco.'
    }
    return `Sessão restaurada. Faltam abrir ${missing.length} IFCs do disco.`
  })()

  return (
    <div className="flex items-start gap-3 border-b border-[var(--color-warn-border)] bg-[var(--color-accent-bg)] px-4 py-2 text-xs text-[var(--color-warn-text)]">
      <div className="flex-1">
        <div>
          <strong className="text-amber-50">{headerCopy}</strong>
          {autoRestoreState !== 'running' && missing.length > 0 && (
            <>
              {' '}
              Carrega-os com o botão à direita (multi-selecção: podes
              escolher vários de uma vez).
            </>
          )}
        </div>
        <ul className="mt-1 flex flex-col gap-0.5 font-mono text-[11px] text-amber-50">
          {missing.map((m) => (
            <li key={m.sha256}>
              {m.filename}
              {m.partilhado && !encontrarPorSha(listaPartilhados, m.sha256) && (
                <span className="ml-2 text-[var(--color-warn-text)]/80">
                  · estava partilhado no workspace, mas não está disponível aqui
                </span>
              )}
              {statusByFile[m.sha256] && (
                <span className="ml-2 text-[var(--color-warn-text)]/80">
                  · {statusByFile[m.sha256]}
                </span>
              )}
            </li>
          ))}
        </ul>
        <p className="mt-1 text-[11px] text-[var(--color-warn-text)]/80">
          Os browsers não expõem o caminho completo ao programa, só o
          nome. Os IFCs estavam carregados desta sessão; abre os
          ficheiros do mesmo nome no teu disco.
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <input
          ref={inputRef}
          type="file"
          accept={modelAcceptAttr()}
          multiple
          className="hidden"
          onChange={(e) => {
            onPickFiles(e.target.files)
            e.target.value = ''
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy || !loaderReady || autoRestoreState === 'running'}
          className="rounded border border-[var(--color-warn-border)] bg-[var(--color-accent-bg)] px-2 py-1 text-amber-50 hover:bg-[var(--color-accent-bg)] disabled:opacity-50"
          title={
            !loaderReady
              ? 'Aguarda que o viewer fique pronto.'
              : autoRestoreState === 'running'
                ? 'A tentar restaurar da cache…'
                : 'Abrir os IFCs em falta a partir do disco. Suporta multi-selecção.'
          }
        >
          {busy
            ? 'A carregar…'
            : autoRestoreState === 'running'
              ? 'A reabrir…'
              : 'Carregar do disco'}
        </button>
        <button
          type="button"
          onClick={() => {
            setExpected(null)
            setStatusByFile({})
          }}
          className="rounded border border-[var(--color-warn-border)] px-2 py-1 text-amber-50 hover:bg-[var(--color-accent-bg)]"
        >
          Dispensar
        </button>
      </div>
    </div>
  )
}
